import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MessageCircle, Phone, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Layout from '@/components/layout/Layout';
import BookingModal from '@/components/BookingModal';
import JsonLd, { generateServiceSchema, generateBreadcrumbSchema } from '@/components/seo/JsonLd';
import FAQSection from '@/components/FAQSection';
import SEOHead from '@/components/seo/SEOHead';
import PageBreadcrumb from '@/components/PageBreadcrumb';
import OptimizedImage from '@/components/ui/OptimizedImage';
import ipadImage from '@/assets/ipad-repair.jpg';

const iPadServiceSchema = generateServiceSchema({
  name: 'iPad Repair Service in Patna, Bihar',
  description: 'Professional iPad repair services in Bihar. Screen replacement, battery replacement, charging port repair for all iPad models including iPad Pro, iPad Air, and iPad Mini.',
  url: 'https://ifixappleservicecenter.in/ipad-service',
  ratingValue: 4.8,
  reviewCount: 280,
});

const iPadBreadcrumbSchema = generateBreadcrumbSchema([
  { name: 'Home', url: 'https://ifixappleservicecenter.in/' },
  { name: 'iPad Service', url: 'https://ifixappleservicecenter.in/ipad-service' },
]);

const ipadFaqs = [
  {
    question: 'How much does iPad screen replacement cost in Patna?',
    answer: 'iPad screen replacement costs vary by model and screen type. Contact us at +91 97986 53591 or WhatsApp for an accurate quote for your specific iPad model.',
  },
  {
    question: 'Can you fix iPad charging port issues?',
    answer: 'Yes, we repair iPad charging port issues including debris cleaning, port replacement, and related circuit repairs. Most charging port repairs are completed within 2-4 hours.',
  },
  {
    question: 'Do you repair iPad Pro with Face ID issues?',
    answer: 'Yes, we diagnose and repair Face ID issues on iPad Pro models. This may involve front camera module replacement or sensor calibration depending on the root cause.',
  },
  {
    question: 'Is iPad screen glass replacement available separately?',
    answer: 'For older iPad models with separate glass and LCD, we offer glass-only replacement at lower cost. Newer iPad models have fused displays requiring full screen assembly replacement.',
  },
  {
    question: 'How long is the warranty on iPad repairs?',
    answer: 'All iPad repairs at iFix Apple Care come with a 3-month warranty covering both parts and workmanship. Extended warranty options are available for screen and battery replacements.',
  },
];



const IPadService = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  const services = [
    'Screen Replacement',
    'Battery Replacement',
    'Logic Board Repair',
    'Water Damage Repair',
    'Overheating Issue',
    'Network Issue',
    'Stuck on Logo',
    'Charging Kit Replacement',
    'Storage Upgrade',
    'Touch Not Working',
    'Earpiece Replacement',
    'Speaker Replacement',
    'Face ID Issue',
  ];

  const models = [
    'iPad 6th Gen', 'iPad 7th Gen', 'iPad 8th Gen', 'iPad 9th Gen', 'iPad 10th Gen',
    'iPad Mini 4', 'iPad Mini 5', 'iPad Mini 6',
    'iPad Air 3', 'iPad Air 4', 'iPad Air 5', 'iPad Air M2',
    'iPad Pro 10.5"', 'iPad Pro 11" (1st Gen)', 'iPad Pro 11" (2nd Gen)', 
    'iPad Pro 11" (3rd Gen)', 'iPad Pro 11" (4th Gen)', 'iPad Pro 11" M4',
    'iPad Pro 12.9" (3rd Gen)', 'iPad Pro 12.9" (4th Gen)', 
    'iPad Pro 12.9" (5th Gen)', 'iPad Pro 12.9" (6th Gen)', 'iPad Pro 13" M4',
  ];

  return (
    <Layout>
      <SEOHead
        title="iPad Repair Services Patna Bihar | iPad Screen Replacement Near Me - iFix"
        description="Best iPad repair services in Patna, Bihar. Expert iPad repair for screen, battery & charging port for iPad, Mini, Air & Pro models. Apple service center near me with same-day service. Get a free quote!"
        url="https://ifixappleservicecenter.in/ipad-service"
      />
      <JsonLd data={iPadServiceSchema} />
      <JsonLd data={iPadBreadcrumbSchema} />
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <OptimizedImage
            src={ipadImage}
            alt="iPad repair service center in Patna Bihar - screen replacement charging port battery fix"
            className="w-full h-full object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/60" />
        </div>
        
        <div className="container-apple relative z-10 py-20">
          <PageBreadcrumb 
            items={[{ name: 'iPad Service', path: '/ipad-service' }]} 
            className="mb-8"
          />
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-foreground leading-tight mb-6 opacity-0 animate-fade-in">
            iPad Service Center
            <span className="block text-primary">in Patna, Bihar</span>
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl leading-relaxed opacity-0 animate-fade-in animation-delay-100">
            Looking for iPad repair services near me? iFix Apple Care offers professional iPad repair in Bihar. From screen repairs to charging issues, 
            we fix all iPad models with expert care at our Apple service center in Patna.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in animation-delay-200">
            <Button variant="apple-blue" size="lg" onClick={() => setIsBookingOpen(true)}>
              <Calendar className="w-5 h-5" />
              Book iPad Repair
            </Button>
            <Button variant="whatsapp" size="lg" asChild>
              <a
                href="https://wa.me/919798653591?text=Hi,%20I%20need%20help%20with%20my%20iPad"
                target="_blank"
                rel="noopener noreferrer"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp
              </a>
            </Button>
            <Button variant="call" size="lg" asChild>
              <a href="tel:9798653591">
                <Phone className="w-5 h-5" />
                Call Now
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding">
        <div className="container-apple">
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 text-center">
            iPad Repair Services
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            Complete iPad repair services for iPad, iPad Mini, iPad Air, and iPad Pro models. Visit our Apple service centre in Patna for expert iPad repair near me.
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {services.map((service, index) => (
              <div
                key={service}
                className="flex items-center gap-3 p-4 bg-card rounded-2xl border border-border hover:border-primary/30 hover:shadow-apple-sm transition-all opacity-0 animate-fade-in"
                style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'forwards' }}
              >
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-primary" />
                </div>
                <span className="font-medium text-foreground">{service}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supported Models */}
      <section className="section-padding bg-secondary">
        <div className="container-apple">
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 text-center">
            Supported iPad Models
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            We repair all iPad models from iPad 6th Gen to the latest iPad Pro models.
          </p>
          
          <div className="flex flex-wrap justify-center gap-3">
            {models.map((model) => (
              <span
                key={model}
                className="px-4 py-2 bg-background rounded-full text-sm font-medium text-foreground border border-border"
              >
                {model}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Related Content */}
      <section className="section-padding">
        <div className="container-apple">
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-8 text-center">
            Related Resources
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link
              to="/blog/ipad-repair-patna-complete-guide"
              className="p-6 bg-card rounded-2xl border border-border hover:border-primary/30 hover:shadow-apple-sm transition-all group"
            >
              <h3 className="font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                iPad Repair Guide
              </h3>
              <p className="text-sm text-muted-foreground">
                Complete guide to iPad repair services in Patna.
              </p>
            </Link>
            <Link
              to="/iphone-service"
              className="p-6 bg-card rounded-2xl border border-border hover:border-primary/30 hover:shadow-apple-sm transition-all group"
            >
              <h3 className="font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                iPhone Repair Services
              </h3>
              <p className="text-sm text-muted-foreground">
                Expert iPhone screen and battery repair.
              </p>
            </Link>
            <Link
              to="/macbook-service"
              className="p-6 bg-card rounded-2xl border border-border hover:border-primary/30 hover:shadow-apple-sm transition-all group"
            >
              <h3 className="font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                MacBook Repair Services
              </h3>
              <p className="text-sm text-muted-foreground">
                Professional MacBook repair service.
              </p>
            </Link>
          </div>
          
          {/* Cross-linking */}
          <div className="mt-12 text-center">
            <p className="text-muted-foreground mb-4">Explore more Apple repair services:</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/iphone-service" className="text-primary hover:underline font-medium">
                iPhone Repair
              </Link>
              <span className="text-border">•</span>
              <Link to="/macbook-service" className="text-primary hover:underline font-medium">
                MacBook Repair
              </Link>
              <span className="text-border">•</span>
              <Link to="/watch-service" className="text-primary hover:underline font-medium">
                Apple Watch Repair
              </Link>
              <span className="text-border">•</span>
              <Link to="/blog" className="text-primary hover:underline font-medium">
                All Repair Guides
              </Link>
              <span className="text-border">•</span>
              <Link to="/#contact" className="text-primary hover:underline font-medium">
                Visit Store
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection
        title="iPad Repair FAQs"
        subtitle="Common questions about our iPad repair services in Patna"
        faqs={ipadFaqs}
      />

      {/* CTA Section */}
      <section className="section-padding bg-foreground">
        <div className="container-apple text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-background mb-4">
            Need iPad Repair?
          </h2>
          <p className="text-background/70 mb-8 max-w-xl mx-auto">
            Get your iPad fixed by certified experts. Professional screen replacement and battery service available.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="apple-blue" size="lg" onClick={() => setIsBookingOpen(true)}>
              <Calendar className="w-5 h-5" />
              Book Appointment
            </Button>
            <Button variant="call" size="lg" asChild>
              <a href="tel:9798653591">
                <Phone className="w-5 h-5" />
                Call Now
              </a>
            </Button>
          </div>
        </div>
      </section>

      <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} />
    </Layout>
  );
};

export default IPadService;
